import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../config/supabase';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

const InterestsMapPage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const [communesPolygons, setCommunesPolygons] = useState([]);
  const [loadingPolygons, setLoadingPolygons] = useState(false);
  const [interestsCount, setInterestsCount] = useState({});
  const [showMarkers, setShowMarkers] = useState(false);
  const [totalInterests, setTotalInterests] = useState(0);
  const [tileProvider, setTileProvider] = useState('osm');
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Récupérer le fond de carte configuré par l'administrateur
  useEffect(() => {
    const fetchTileParameter = async () => {
      const { data, error } = await supabase
        .from('parameters')
        .select('value')
        .eq('parameter', 'tile')
        .single();

      if (!error && data?.value) {
        setTileProvider(String(data.value).trim().toLowerCase());
      }
    };

    fetchTileParameter();
  }, []);

  // Récupérer les polygones des communes avec des intérêts
  useEffect(() => {
    const fetchInterestsData = async () => {
      setLoadingPolygons(true);
      try {
        // Récupérer les intérêts groupés par code commune INSEE
        const { data: interestsData, error: interestsError } = await supabase
          .from('interested')
          .select('code_commune_insee');

        if (interestsError) {
          console.error('[Interests] Erreur récupération intérêts:', interestsError);
          return;
        }

        if (!interestsData || interestsData.length === 0) {
          console.log('[Interests] Aucun intérêt trouvé');
          setCommunesPolygons([]);
          setInterestsCount({});
          setTotalInterests(0);
          return;
        }

        // Compter les intérêts par code commune INSEE
        const counts = interestsData.reduce((acc, interest) => {
          const inseeCode = interest.code_commune_insee;
          if (inseeCode) {
            acc[inseeCode] = (acc[inseeCode] || 0) + 1;
          }
          return acc;
        }, {});

        console.log('[Interests] Nombre d\'intérêts par code INSEE:', counts);
        setInterestsCount(counts);
        setTotalInterests(interestsData.length);

        const inseeCodes = Object.keys(counts);
        console.log('[Interests] Codes INSEE à récupérer:', inseeCodes.length);

        // Récupérer les noms des communes
        const { data: communesData, error: communesError } = await supabase
          .from('communes')
          .select('code_commune_insee, nom_de_la_commune')
          .in('code_commune_insee', inseeCodes);

        if (communesError) {
          console.error('[Interests] Erreur récupération communes:', communesError);
          return;
        }

        // Créer un mapping code_commune_insee -> noms de communes
        const inseeCodeToNames = communesData?.reduce((acc, commune) => {
          if (!acc[commune.code_commune_insee]) {
            acc[commune.code_commune_insee] = [];
          }
          if (commune.nom_de_la_commune && !acc[commune.code_commune_insee].includes(commune.nom_de_la_commune)) {
            acc[commune.code_commune_insee].push(commune.nom_de_la_commune);
          }
          return acc;
        }, {}) || {};

        // Récupérer les polygones correspondants
        const { data: polygonsData, error: polygonsError } = await supabase
          .from('communes')
          .select('code_commune_insee, geom')
          .in('code_commune_insee', inseeCodes)
          .not('geom', 'is', null);

        if (polygonsError) {
          console.error('[Interests] Erreur récupération polygones:', polygonsError);
          return;
        }

        // Enrichir les polygones avec les noms des communes
        const enrichedPolygons = polygonsData?.map(polygon => ({
          ...polygon,
          nom: inseeCodeToNames[polygon.code_commune_insee]?.join(', ') || t('common.unknown')
        })) || [];

        console.log('[Interests] Polygones enrichis:', enrichedPolygons?.length);
        setCommunesPolygons(enrichedPolygons);

      } catch (error) {
        console.error('[Interests] Erreur:', error);
      } finally {
        setLoadingPolygons(false);
      }
    };

    if (isAdmin) {
      fetchInterestsData();
    }
  }, [isAdmin, t]);

  // Fonction pour obtenir la couleur selon le nombre d'intérêts
  const getColorByInterestCount = (count) => {
    if (!count || count === 0) return '#94a3b8'; // gris si pas d'intérêts
    
    // Dégradé du gris vers le vert rgb(34, 197, 94) avec progression visible
    const maxCount = 20; // Ajuster selon les besoins
    const ratio = Math.min(count / maxCount, 1);
    
    // Couleur de départ (gris clair) : rgb(148, 163, 184)
    const startR = 148;
    const startG = 163;
    const startB = 184;
    
    // Couleur d'arrivée (vert vif) : rgb(34, 197, 94)
    const endR = 34;
    const endG = 197;
    const endB = 94;
    
    // Interpolation linéaire avec courbe exponentielle
    const adjustedRatio = Math.pow(ratio, 0.7);
    const r = Math.round(startR + (endR - startR) * adjustedRatio);
    const g = Math.round(startG + (endG - startG) * adjustedRatio);
    const b = Math.round(startB + (endB - startB) * adjustedRatio);
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Initialiser et mettre à jour la carte
  useEffect(() => {
    if (!mapRef.current) return;

    // Initialiser la carte si nécessaire
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([46.603354, 1.888334], 6);

      const baseLayers = {
        osm: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }),
        stadiaSmooth: L.tileLayer(`https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        stadiaSmoothDark: L.tileLayer(`https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        stradiamaps: L.tileLayer(`https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        stadiaOSMBright: L.tileLayer(`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`, {
          attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        }),
        satellite: L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          attribution: '&copy; <a href="https://www.esri.com">Esri</a>'
        }),
        terrain: L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        })
      };

      const layerNames = {
        osm: t('map.standard'),
        stadiaSmooth: t('map.stadiaSmooth'),
        stadiaSmoothDark: t('map.stadiaSmoothDark'),
        stradiamaps: t('map.stadiaOutdoors'),
        stadiaOSMBright: t('map.stadiaOSMBright'),
        satellite: t('map.satellite'),
        terrain: t('map.terrain')
      };
      const selectedProvider = baseLayers[tileProvider] ? tileProvider : 'osm';
      baseLayers[selectedProvider].addTo(mapInstanceRef.current);
      L.control.layers(
        Object.fromEntries(Object.entries(baseLayers).map(([key, layer]) => [layerNames[key], layer])),
        undefined,
        { position: 'topright' }
      ).addTo(mapInstanceRef.current);
    }

    // Nettoyer les couches existantes
    mapInstanceRef.current.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON || layer instanceof L.Marker) {
        mapInstanceRef.current.removeLayer(layer);
      }
    });

    const bounds = L.latLngBounds([]);

    // Afficher les communes avec intérêts
    if (communesPolygons.length > 0) {
      communesPolygons.forEach(commune => {
        try {
          const geojson = typeof commune.geom === 'string' 
            ? JSON.parse(commune.geom) 
            : commune.geom;

          const communeName = commune.nom || t('common.unknown');
          const inseeCode = commune.code_commune_insee || '';
          const interestCount = interestsCount[inseeCode] || 0;
          const color = getColorByInterestCount(interestCount);
          
          const geoJsonLayer = L.geoJSON(geojson, {
            style: {
              color: color,
              weight: 2,
              opacity: 1,
              fillOpacity: 0.4,
              fillColor: color
            }
          }).bindPopup(`<b>${communeName}</b>${inseeCode ? `<br>${inseeCode}` : ''}<br>${interestCount} ${t('settings.admin.interestsMap.interests')}`);

          geoJsonLayer.addTo(mapInstanceRef.current);
          bounds.extend(geoJsonLayer.getBounds());

          // Marqueur avec le dégradé vert
          if (showMarkers) {
            const center = geoJsonLayer.getBounds().getCenter();
            
            const divIcon = L.divIcon({
              className: 'interest-count-marker',
              html: `<div style="
                background: ${color};
                color: white;
                border: 3px solid white;
                border-radius: 50%;
                width: ${Math.min(70, 35 + Math.sqrt(interestCount) * 4)}px;
                height: ${Math.min(70, 35 + Math.sqrt(interestCount) * 4)}px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: ${interestCount > 99 ? '11px' : '15px'};
                box-shadow: 0 4px 8px rgba(0,0,0,0.4);
                transition: all 0.3s ease;
              ">${interestCount}</div>`,
              iconSize: [Math.min(70, 35 + Math.sqrt(interestCount) * 4), Math.min(70, 35 + Math.sqrt(interestCount) * 4)],
              iconAnchor: [Math.min(70, 35 + Math.sqrt(interestCount) * 4) / 2, Math.min(70, 35 + Math.sqrt(interestCount) * 4) / 2]
            });

            const marker = L.marker(center, { icon: divIcon });
            marker.bindTooltip(`${communeName}<br>${inseeCode}<br>${interestCount} ${t('settings.admin.interestsMap.interests')}`, {
              permanent: false,
              direction: 'top'
            });
            marker.addTo(mapInstanceRef.current);
          }
        } catch (error) {
          console.error('[Interests] Erreur parsing géométrie:', error, commune);
        }
      });

      if (bounds.isValid()) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
      }
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [communesPolygons, interestsCount, showMarkers, tileProvider, t]);

  if (adminLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <style>{`
        .interests-map .leaflet-top,
        .interests-map .leaflet-bottom {
          z-index: 1000;
        }

        .interests-map .leaflet-control-layers-toggle {
          background-color: white;
          color: #2894D8;
        }

        .interests-map .leaflet-control-layers-toggle:hover {
          background-color: #2894D8;
        }

        .interests-map .leaflet-control-layers {
          border: 0;
        }
      `}</style>
      <PageHeader title={t('settings.admin.interestsMap.title')} backPath="/settings" />

      <div className="p-4">
        {/* Statistiques */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('settings.admin.interestsMap.totalInterests')}</p>
              <p className="text-2xl font-bold text-[#2894D8]">{totalInterests}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('settings.admin.interestsMap.communesWithInterests')}</p>
              <p className="text-2xl font-bold text-[#22c55e]">{Object.keys(interestsCount).length}</p>
            </div>
          </div>
        </div>

        {/* Carte des intérêts */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-h2">
              {t('settings.admin.interestsMap.mapTitle')}
            </h2>
            <button
              onClick={() => setShowMarkers(!showMarkers)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                showMarkers 
                  ? 'bg-[#2894D8] text-white hover:bg-[#136E9D]' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {showMarkers ? t('settings.admin.interestsMap.hideMarkers') : t('settings.admin.interestsMap.showMarkers')}
            </button>
          </div>
          {loadingPolygons ? (
            <div className="flex items-center justify-center h-[600px]">
              <LoadingSpinner />
            </div>
          ) : communesPolygons.length === 0 ? (
            <div className="h-[600px] flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-500">{t('settings.admin.interestsMap.noInterests')}</p>
            </div>
          ) : (
            <div
              ref={mapRef} 
              className="interests-map h-[600px] rounded-lg border border-gray-200"
              style={{ zIndex: 1 }}
            />
          )}
          {communesPolygons.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {communesPolygons.length} {communesPolygons.length > 1 ? t('settings.admin.interestsMap.communes') : t('settings.admin.interestsMap.commune')} {t('settings.admin.interestsMap.displayed')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterestsMapPage;
